import { merge, castArray } from 'lodash';
import { map } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { ISnListQueryParameters } from '@lib/api';
import { SnAppliance, SnPullProtocol } from '@lib/api/entities';
import { SnAppliancesAPI } from '@lib/api/services';
import { NETWORK_PROTOCOL_SPECS } from '@lib/common';
import { SnFormFieldConfig } from '@lib/form';
import { ISnHttpSelectFormFieldConfig } from '@lib/form/http-select';
import { ISnInputFormFieldConfig } from '@lib/form/input';
import { t } from '@lib/translation';

import { ISnApplianceSelectFieldOptions } from '../interfaces/appliance-select-field-options';

@Injectable({ providedIn: 'root' })
export class SnApplianceFieldsGeneratorService {
  constructor(private readonly _appliancesAPI: SnAppliancesAPI) {}

  public getApplianceSelectField(
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<SnAppliance>>,
    options: ISnApplianceSelectFieldOptions = {},
  ): ISnHttpSelectFormFieldConfig<SnAppliance> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<SnAppliance> = {
      key: 'appliance',
      type: 'http-select',
      templateOptions: {
        label: t('appliances.labels.fususCore'),
        search: true,
        searchParameter: 'search',
        placeholder: t('common.selectFususCore'),
        valueProp: 'id',
        labelProp: 'name',
        infinityScroll: true,
        fetchDataFn: (fetchParams: ISnListQueryParameters) => {
          return this._appliancesAPI.getApplianceList({ ...options.params, ...fetchParams }).pipe(
            map((listResponse) => {
              if (options.exclude) {
                const excludeList = castArray(options.exclude);
                const filteredResult = listResponse.results.filter(
                  (appliance) => !excludeList.includes(appliance.id),
                );

                return {
                  ...listResponse,
                  results: filteredResult,
                };
              }

              return listResponse;
            }),
          );
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getNameField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'name',
      type: 'input',
      templateOptions: {
        label: t('common.name'),
        placeholder: t('common.enterName'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getIsCloudField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'isCloud',
      type: 'drag-slide-toggle',
      templateOptions: {
        appearance: 'fill',
        toggleLabel: 'Cloud',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getMakeField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'make',
      type: 'input',
      templateOptions: {
        label: t('appliances.labels.make'),
        placeholder: t('common.enterMake'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getModelField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'model',
      type: 'input',
      templateOptions: {
        label: t('common.model'),
        placeholder: t('common.enterModel'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getSerialNumberField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'serialNumber',
      type: 'input',
      templateOptions: {
        label: t('appliances.labels.serialNumber'),
        placeholder: t('common.enterSerialNumber'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getServerIdField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'serverId',
      type: 'input',
      templateOptions: {
        label: t('appliances.labels.serverId'),
        placeholder: t('common.enterServerId'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getHostField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'host',
      type: 'input',
      templateOptions: {
        label: 'Host',
        placeholder: 'Enter host',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getRtmpPortField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'rtmpPort',
      type: 'input',
      templateOptions: {
        label: `${NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTMP} Port`,
        placeholder: '1935',
        type: 'number',
      },
      validators: {
        validation: ['port'],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getRtspPortField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'rtspPort',
      type: 'input',
      templateOptions: {
        label: `${NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTSP} Port`,
        placeholder: '554',
        type: 'number',
      },
      validators: {
        validation: ['port'],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getHttpPortField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'httpPort',
      type: 'input',
      templateOptions: {
        label: `${NETWORK_PROTOCOL_SPECS.PROTOCOLS.HTTP} Port`,
        placeholder: '80',
        type: 'number',
      },
      validators: {
        validation: ['port'],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPullProtocolField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'pullProtocol',
      type: 'radio',
      templateOptions: {
        appearance: 'fill',
        label: 'Protocol',
        options: [
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTSP, value: SnPullProtocol.RTSP },
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTMP, value: SnPullProtocol.RTMP },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPullSslField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'pullSsl',
      type: 'drag-slide-toggle',
      templateOptions: {
        appearance: 'fill',
        toggleLabel: NETWORK_PROTOCOL_SPECS.PROTOCOLS.SSL,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }
}
